/**
 * State filing credentials (Partners screen, Jerry 2026-07-22: "a section to load the API key per
 * state on the Partners screen").
 *
 * MISC enters, per client, the API credential each state's filing system needs. The SHAPE of that
 * credential is decided by the state's contract, NOT by the frontend:
 *   - Client-login states (SLIP+ for TN/SD/CO/LA, FSLSO for FL): the client's own API key (+ a
 *     username where the system uses one; FSLSO has none). Entered here.
 *   - MISC-login states (SLAcal CA, SLTX TX): one shared MISC login files for everyone, so what the
 *     client enters HERE is only its identifier (SLA broker number / SLTX filer id). The shared MISC
 *     login itself is shown read-only for context and is managed elsewhere.
 *
 * WRITE-ONLY, BY DESIGN: a stored API key / broker number is NEVER returned by any endpoint. The list
 * read surfaces only a `*HasSecret` / `*HasValue` flag plus metadata (when it was set, whether it is
 * approved, when it was last used/verified). A value can be SET or REPLACED, never read back. This
 * mirrors the credential store's rule and there is deliberately no "reveal" call to add.
 *
 * REMOVAL + APPROVAL are separate, deliberate acts:
 *   - A newly saved / replaced credential lands UNAPPROVED — it cannot file until an admin approves it.
 *   - Removal (deactivate) and revoke are admin-only, audited server-side (who + when).
 *
 * ── Endpoint contract (this FE is built to it; the internal-api handlers implement it) ──
 *   GET  /AutomaticFiling/stateFilingCredentials?partnersId=<id>   → { status, data: StateCredentialRow[] }
 *   POST /AutomaticFiling/stateFilingCredential
 *        Client-scope body { partnersId, state, secret, username?, secretKind?, label? }
 *        MISC-scope  body { partnersId, state, value, label? }        (value = broker number / filer id)
 *        → { status, data: { id, requiresApproval: true } }
 *   POST /AutomaticFiling/stateFilingCredential/approve     body { credentialId } → { status }
 *   POST /AutomaticFiling/stateFilingCredential/revoke      body { credentialId } → { status }
 *   POST /AutomaticFiling/stateFilingCredential/deactivate  body { credentialId } → { status }
 *   POST /AutomaticFiling/stateFilingCredentials/verify     body { partnersId, state } → { status, data: VerifyCredentialData }
 *
 * VERIFY is a SECOND, INDEPENDENT axis from approval: approval is a human sign-off (a SOC control),
 * verification is "does this stored credential actually authenticate against the state clearinghouse".
 * A credential can be approved but technically broken, or reachable but unapproved. Neither gates the
 * other. The call is rate limited SERVER-SIDE (1/min per client+state) — the FE never reimplements that
 * timing, it only honours a `cooldown` response.
 *
 * Until the backend is deployed, GET returns 404 → the panel renders scaffolded with a "not enabled
 * yet" banner, exactly like ClientFilingDefaultsPanel.
 */
import { apiClient } from './api';

export type CredentialOwnerScope = 'Client' | 'Misc';

export interface StateCredentialRow {
    stateId: number;
    postalAbbv: string;
    stateName: string;
    filingSystem: string;
    filingSystemLabel: string;
    credentialOwnerScope: CredentialOwnerScope;
    requiredIdentifierType: string | null;
    requiredIdentifierLabel: string | null;
    /** False for FSLSO (Florida): its form hides the username field. */
    usesUsername: boolean;
    secretLabel: string;               // 'API key'
    supportsVerify: boolean;

    // This client's own login (Client-scope). Never the value — only its presence + metadata.
    credentialId: number | null;
    credUsername: string | null;
    credSecretKind: string | null;
    credLabel: string | null;
    credHasSecret: boolean;
    credApproved: boolean;
    credApprovedByName: string | null;
    credApprovedAt: string | null;
    credSecretSetAt: string | null;
    credLastUsedAt: string | null;
    /** Last SUCCESSFUL authentication (`cred.LastVerifiedAt`). Stamped on success only — a failed test
     *  leaves it untouched, so it is a "last known good" marker, not a current-health flag. */
    credLastVerifiedAt: string | null;

    // This client's identifier (MISC-scope, e.g. CA broker number). Encrypted at rest; presence only.
    identifierId: number | null;
    identifierType: string | null;
    identLabel: string | null;
    identHasValue: boolean;
    identApproved: boolean;
    identApprovedByName: string | null;
    identApprovedAt: string | null;
    identSetAt: string | null;
    identLastUsedAt: string | null;

    // This client's LINKED LOGIN (Client-scope alternative to an API key, Jerry 2026-07-24): a pointer
    // to a State Login. Only the referenced username is surfaced, never a password.
    linkedCredentialId: number | null;
    linkedStateLoginsId: number | null;
    linkedUsername: string | null;
    linkedLicensee: string | null;
    linkedApproved: boolean;
    linkedApprovedByName: string | null;
    linkedLastUsedAt: string | null;

    // The shared MISC login for the state (read-only context on MISC-scope rows).
    miscCredentialId: number | null;
    miscUsername: string | null;
    miscHasSecret: boolean;
    miscApproved: boolean;
}

export interface StateLoginOption {
    stateLoginsId: number;
    licensee: string | null;
    username: string | null;
}

/** A client's default login for a filing system (SLIP+ "one login across its states"). */
export interface FilingLoginDefault {
    partnerFilingLoginDefaultId: number;
    filingSystem: string;
    stateLoginsId: number;
    loginUsername: string | null;
    loginLicensee: string | null;
    approvedForSubmit: boolean;
    approvedByName: string | null;
    /**
     * When this default login LAST AUTHENTICATED SUCCESSFULLY (last known good).
     *
     * `dbo.PartnerFilingLoginDefault` historically had no such column — only `StateFilingCredential`
     * carried `LastVerifiedAt`, and a SLIP+ default has no row there, so a verified SLIP+ default had
     * nowhere to persist its verdict. The column is being added and surfaced on this payload; the
     * field name is not frozen yet, so BOTH spellings are accepted and BOTH are optional. Read it
     * through `defaultLastVerified()` rather than touching these directly — when the backend has not
     * shipped it yet the panel must degrade to "not tested yet", never break.
     */
    lastVerifiedAt?: string | null;
    defaultLastVerifiedAt?: string | null;
}

/**
 * The default's last-known-good timestamp, tolerant of the backend field name and of its absence.
 *
 * `LastVerifiedAt` is stamped ONLY on a successful authentication and deliberately left alone on a
 * failure, so a value here always means "this DID work at that moment" — never "this works now".
 * Callers render the age alongside it; a stale success is not the same claim as a fresh one.
 */
export function defaultLastVerified(def?: FilingLoginDefault | null): string | null {
    if (!def) return null;
    const raw = def.defaultLastVerifiedAt ?? def.lastVerifiedAt ?? null;
    return typeof raw === 'string' && raw.trim() !== '' ? raw : null;
}

export async function getFilingLoginDefaults(token: string | undefined, partnersId: number): Promise<FilingLoginDefault[]> {
    const res: any = await apiClient(`/AutomaticFiling/partnerFilingLoginDefaults?partnersId=${partnersId}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res || res.ok === false) return [];
    return Array.isArray(res.data) ? (res.data as FilingLoginDefault[]) : [];
}

export async function getSystemLoginOptions(token: string | undefined, partnersId: number, filingSystem: string): Promise<StateLoginOption[]> {
    const res: any = await apiClient(`/AutomaticFiling/stateLoginSystemOptions?partnersId=${partnersId}&filingSystem=${encodeURIComponent(filingSystem)}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res || res.ok === false) return [];
    return Array.isArray(res.data) ? (res.data as StateLoginOption[]) : [];
}

export async function setFilingLoginDefault(token: string | undefined, payload: { partnersId: number; filingSystem: string; stateLoginsId: number; label?: string | null }): Promise<MutationResult> {
    const res: any = await apiClient('/AutomaticFiling/filingLoginDefault', {
        method: 'POST', headers: authHeaders(token), body: JSON.stringify(payload),
    });
    return toMutationResult(res);
}

async function mutateDefault(token: string | undefined, action: 'approve' | 'remove', defaultId: number): Promise<MutationResult> {
    const res: any = await apiClient(`/AutomaticFiling/filingLoginDefault/${action}`, {
        method: 'POST', headers: authHeaders(token), body: JSON.stringify({ defaultId }),
    });
    return toMutationResult(res);
}
export const approveFilingLoginDefault = (token: string | undefined, defaultId: number) => mutateDefault(token, 'approve', defaultId);
export const removeFilingLoginDefault = (token: string | undefined, defaultId: number) => mutateDefault(token, 'remove', defaultId);

export interface CredentialListResult {
    ok: boolean;
    /** True when the backend isn't available yet (e.g. 404 pre-deploy). */
    unavailable: boolean;
    rows: StateCredentialRow[];
}

export interface MutationResult {
    ok: boolean;
    unavailable: boolean;
    message?: string;
    /** For a save: the new credential id (unapproved). */
    id?: number;
}

function authHeaders(token?: string): HeadersInit {
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export async function getPartnerCredentials(token: string | undefined, partnersId: number): Promise<CredentialListResult> {
    const res: any = await apiClient(`/AutomaticFiling/stateFilingCredentials?partnersId=${partnersId}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res || res.ok === false) return { ok: false, unavailable: true, rows: [] };
    return { ok: true, unavailable: false, rows: Array.isArray(res.data) ? (res.data as StateCredentialRow[]) : [] };
}

export interface SaveCredentialPayload {
    partnersId: number;
    state: string;              // postal abbreviation, e.g. 'TN'
    /** Client-scope, API-key mode: the API key. */
    secret?: string;
    username?: string | null;
    secretKind?: 'ApiKey' | 'Password';
    /** Client-scope, linked-login mode: the chosen State Login (Jerry 2026-07-24). Either/or with secret. */
    stateLoginsId?: number;
    /** MISC-scope: the identifier value (broker number / filer id). */
    value?: string;
    label?: string | null;
}

/** The State Logins (username only) this client has for a state — the "link a login" dropdown source. */
export async function getStateLoginOptions(token: string | undefined, partnersId: number, stateId: number): Promise<StateLoginOption[]> {
    const res: any = await apiClient(`/AutomaticFiling/stateLoginOptions?partnersId=${partnersId}&stateId=${stateId}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res || res.ok === false) return [];
    return Array.isArray(res.data) ? (res.data as StateLoginOption[]) : [];
}

export async function saveCredential(token: string | undefined, payload: SaveCredentialPayload): Promise<MutationResult> {
    // sensitive=true: the body carries a plaintext API key / broker number, so apiClient must keep it out
    // of Sentry telemetry on a failed request.
    const res: any = await apiClient('/AutomaticFiling/stateFilingCredential', {
        method: 'POST', headers: authHeaders(token), body: JSON.stringify(payload),
    }, undefined, true);
    return toMutationResult(res);
}

async function mutateById(token: string | undefined, action: 'approve' | 'revoke' | 'deactivate', credentialId: number): Promise<MutationResult> {
    const res: any = await apiClient(`/AutomaticFiling/stateFilingCredential/${action}`, {
        method: 'POST', headers: authHeaders(token), body: JSON.stringify({ credentialId }),
    });
    return toMutationResult(res);
}

export const approveCredential = (token: string | undefined, credentialId: number) => mutateById(token, 'approve', credentialId);
export const revokeCredential = (token: string | undefined, credentialId: number) => mutateById(token, 'revoke', credentialId);
export const removeCredential = (token: string | undefined, credentialId: number) => mutateById(token, 'deactivate', credentialId);

export type VerifyStatus = 'connected' | 'failed' | 'cooldown';

/** The server's verification verdict. `message` is safe to display verbatim (never carries a secret). */
export interface VerifyCredentialData {
    ok: boolean;
    status: VerifyStatus;
    message: string;
    filingSystem: string | null;
    /** The environment host that answered, e.g. https://sliptest.slasclearinghouse.com — shown so it is
     *  unambiguous which environment responded (staging must talk to sandbox hosts). */
    host: string | null;
    checkedAt: string;
    /** Present only when status === 'cooldown'. */
    retryAfterSeconds?: number;
}

export interface VerifyResult {
    ok: boolean;
    unavailable: boolean;
    message?: string;
    /** Present only when ok. */
    data?: VerifyCredentialData;
}

/** Ask the server to authenticate this client's stored credential for a state against the live filing
 *  system. Never called on render — only from an explicit "Test connection" or right after an approve. */
export async function verifyCredential(token: string | undefined, partnersId: number, state: string): Promise<VerifyResult> {
    const res: any = await apiClient('/AutomaticFiling/stateFilingCredentials/verify', {
        method: 'POST', headers: authHeaders(token), body: JSON.stringify({ partnersId, state }),
    });
    const base = toMutationResult(res);
    if (!base.ok) return { ok: false, unavailable: base.unavailable, message: base.message };
    const d = res?.data;
    if (!d || typeof d.status !== 'string') {
        return { ok: false, unavailable: false, message: 'Verification returned no result.' };
    }
    return { ok: true, unavailable: false, data: d as VerifyCredentialData };
}

function toMutationResult(res: any): MutationResult {
    if (!res) return { ok: false, unavailable: false, message: 'Network error — try again.' };
    // apiClient sets ok:false ONLY on a non-2xx HTTP response (there `status` is the HTTP number).
    if (res.ok === false) {
        return { ok: false, unavailable: res.status === 404, message: res.message || `Request failed (HTTP ${res.status}).` };
    }
    // A 2xx response returns the raw body per the `{ status, data }` contract — a business failure comes
    // back as `status: false` at HTTP 200. Without this, a failed save/approve/revoke would read as
    // success (clearing the entered secret and reloading). `status` here is a boolean, never the HTTP
    // number (that path is caught above), so this cannot false-trigger on a real success (`status: true`).
    if (res.status === false) {
        return { ok: false, unavailable: false, message: res.message || 'Request failed.' };
    }
    return { ok: true, unavailable: false, id: res.data?.id };
}
